import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * 🎟️ Estructura del ticket de impresión retornado desde el backend.
 */
export interface TicketPrintPayload {
  logo: string;   // Imagen del logo en Base64
  ticket: string; // Texto ESC/POS del ticket en Base64
  qr: string;     // Imagen del código QR en Base64
}

interface PrintingState {
  /**
   * 🧾 Payload completo del ticket recibido desde el backend (modo CLOUD)
   */
  ticketData: TicketPrintPayload | null;

  /**
   * ⚙️ Indica si se está procesando la impresión
   */
  isPrinting: boolean;

  /**
   * 🧠 ID de la venta o compra actualmente en proceso de impresión
   */
  currentId: number | null;

  /**
   * ✅ Indica si la impresión fue exitosa
   */
  success: boolean;

  /**
   * ❌ Mensaje de error si algo falla
   */
  error: string | null;
}

const initialState: PrintingState = {
  ticketData: null,
  isPrinting: false,
  currentId: null,
  success: false,
  error: null,
};

const printingSlice = createSlice({
  name: 'printing',
  initialState,
  reducers: {
    /**
     * 🚀 Inicia el proceso de impresión (guardamos el ID actual)
     */
    startPrinting(state, action: PayloadAction<number>) {
      state.isPrinting = true;
      state.currentId = action.payload;
      state.success = false;
      state.error = null;
    },

    /**
     * 🧾 Guarda el payload recibido desde el backend (logo, ticket y QR)
     */
    setTicketData(state, action: PayloadAction<TicketPrintPayload>) {
      state.ticketData = action.payload;
      state.isPrinting = false;
      state.success = true;
      state.error = null;
    },

    /**
     * ❌ Marca un error de impresión
     */
    setPrintingError(state, action: PayloadAction<string>) {
      state.isPrinting = false;
      state.success = false;
      state.error = action.payload;
    },

    /**
     * 🔄 Limpia todo el estado de impresión
     */
    clearPrintingState(state) {
      state.ticketData = null;
      state.isPrinting = false;
      state.success = false;
      state.error = null;
      state.currentId = null;
    },
  },
});

export const {
  startPrinting,
  setTicketData,
  setPrintingError,
  clearPrintingState,
} = printingSlice.actions;

export default printingSlice.reducer;
